import React, { MouseEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import _ from 'lodash';
import { OrderDirection } from '../Datatable/OrderDirection';
import { invalidMarketInfoSelector, setCorrectInvalidMarketInfoParam, setInvalidMarketInfoData, setInvalidMarketInfoDisplay, setInvalidMarketInfoOrderBy, setInvalidMarketInfoOrderDirection, setInvalidMarketInfoSearchText, setOpenEditInvalidMarketInfoModal, setRefreshInvalidMarketInfoTable, setSelectedItem } from './reducers/InvalidMarketInfoReducer';
import moment from 'moment';
import { Box, Button, Grid, Modal } from '@mui/material';
import MarketInfoApi from '../MarketInfo/api/MarketInfoApi';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import PortSelector from '../PortSelector/PortSelector';
import PortModel from '../PortSelector/models/PortModel';
import { portSelectorSelector } from '../PortSelector/reducers/PortSelectorReducer';
import ContactSelector from '../ContactSelector/ContactSelector';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { InvalidMarketInfoModel } from './models/InvalidMarketInfoModel';
import CargoTypeSelector from '../CargoTypeSelector/CargoTypeSelector';
import CargoTypeModel from '../CargoTypeSelector/models/CargoTypeModel';
import MarketInfoModel from '../MarketInfo/models/MarketInfoModel';
import { cargoTypeSelectorSelector } from '../CargoTypeSelector/reducers/CargoTypeSelectorReducer';

const InvalidMarketInfoTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    let invalidMarketInfoData = useSelector(invalidMarketInfoSelector);
    let portSelectorData = useSelector(portSelectorSelector);
    let cargoTypeSelectorData = useSelector(cargoTypeSelectorSelector);

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "800px",
        bgcolor: 'white',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    useEffect(() => {
        MarketInfoApi.GetInvalidMarketInfos()
            .then((response: BaseResponse<InvalidMarketInfoModel[]>) => {
                if (response.success) {
                    dispatch(setInvalidMarketInfoDisplay(response.data));
                    dispatch(setInvalidMarketInfoData(response.data));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
    }, [invalidMarketInfoData.refreshInvalidMarketInfoTable])

    const columns = [
        {
            Field: "MarketInfoVesselName",
            Title: "Vessel",
            AllowSort: true,
        } as DatatableColumnModel<InvalidMarketInfoModel>,
        {
            Field: "size",
            Title: "Size",
            AllowSort: true,
            Render: (rowData: InvalidMarketInfoModel) => {
                return <>{rowData.size ? rowData.size.toLocaleString() : ""}</>;
            }
        } as DatatableColumnModel<InvalidMarketInfoModel>,
        {
            Field: "type",
            Title: "Biz",
            AllowSort: true,
        } as DatatableColumnModel<InvalidMarketInfoModel>,
        {
            Field: "cargoTypeName",
            Title: "Cargo",
            AllowSort: true,
        } as DatatableColumnModel<InvalidMarketInfoModel>,
        {
            Field: "loadPortName",
            Title: "L/D Port",
            AllowSort: true,
        } as DatatableColumnModel<InvalidMarketInfoModel>,
        {
            Field: "disportName",
            Title: "D/R Port",
            AllowSort: true,
        } as DatatableColumnModel<InvalidMarketInfoModel>,
        {
            Field: "ownerRate",
            Title: "Owners $",
            AllowSort: true,
            Render: (rowData: InvalidMarketInfoModel) => {
                return <>{rowData.ownerRate ? rowData.ownerRate.toLocaleString() : ""}</>;
            }
        } as DatatableColumnModel<InvalidMarketInfoModel>,
        {
            Field: "chartererRate",
            Title: "Chtrs $",
            AllowSort: true,
            Render: (rowData: InvalidMarketInfoModel) => {
                return <>{rowData.chartererRate ? rowData.chartererRate.toLocaleString() : ""}</>;
            }
        } as DatatableColumnModel<InvalidMarketInfoModel>,
        {
            Field: "ownerName",
            Title: "Owners",
            AllowSort: true,
        } as DatatableColumnModel<InvalidMarketInfoModel>,
        {
            Field: "chartererName",
            Title: "Chtrs",
            AllowSort: true,
        } as DatatableColumnModel<InvalidMarketInfoModel>,
        {
            Field: "",
            Title: "",
            AllowSort: false,
            Render: (rowData: InvalidMarketInfoModel) => (
                <>
                    <Button variant='outlined'
                        onClick={() => {
                            dispatch(setCorrectInvalidMarketInfoParam({
                                id: rowData.id,
                                chartererId: rowData.chartererActive ? rowData.chartererId : null,
                                cargoTypeId: rowData.cargoTypeActive ? rowData.cargoTypeId : null,
                                ownerId: rowData.ownerActive ? rowData.ownerId : null,
                                loadPort: rowData.loadPortActive ? rowData.loadPort : null,
                                disport: rowData.disportActive ? rowData.disport : null
                            }))
                            dispatch(setSelectedItem(rowData));
                            dispatch(setOpenEditInvalidMarketInfoModal(true));
                        }}
                    >
                        Correct
                    </Button>
                </>
            )
        } as DatatableColumnModel<InvalidMarketInfoModel>,
    ];

    const formatDateMMMDD = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD MMM");
    };

    const getTableClassName = () => {
        let className = "cargo-type-management-datatable-search-result nice-scroll";
        return className;
    }

    const getPortById = (portId: number) => {
        let selectedPort = _.find(portSelectorData.ports, (port) => {
            return port.id === portId;
        });

        if (selectedPort) {
            return selectedPort;
        }

        return null;
    }

    const getCargoTypeById = (cargoTypeId: number) => {
        let selectedCargoType = _.find(cargoTypeSelectorData.cargoTypes, (cargo) => {
            return cargo.id === cargoTypeId;
        });

        if (selectedCargoType) {
            return selectedCargoType;
        }

        return null;
    }

    return <div className='cargo-type-datatable-wrapper'>
        <Datatable<InvalidMarketInfoModel>
            useDefaultSearchBar={true}
            pagingType={PagingTypeEnum.None}
            data={invalidMarketInfoData.invalidMarketInfoDisplay}
            useItemPerPage={false}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onOrderDirectionChange={(column: DatatableColumnModel<InvalidMarketInfoModel>, orderDirection: OrderDirection) => {
                dispatch(setInvalidMarketInfoOrderBy(column.Field));
                dispatch(setInvalidMarketInfoOrderDirection(orderDirection));

                let data = [...invalidMarketInfoData.invalidMarketInfoDisplay];
                if (orderDirection == OrderDirection.Asc) {
                    data = _.sortBy(data, column.Field);
                } else {
                    data = _.sortBy(data, column.Field).reverse();
                }

                dispatch(setInvalidMarketInfoDisplay(data));
            }}
            orderColumn={invalidMarketInfoData.orderBy}
            orderDirection={invalidMarketInfoData.orderDirection}
            stickyHeader={true}
        ></Datatable>
        <Modal
            open={invalidMarketInfoData.openEditInvalidMarketInfoModal}
            onClose={() => {
                dispatch(setOpenEditInvalidMarketInfoModal(false));
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={style}>
                {
                    invalidMarketInfoData.selectedItem &&
                    <Grid container>
                        {
                            !invalidMarketInfoData.selectedItem.chartererActive &&
                            <Grid item xs={12} className="input-component">
                                <ContactSelector
                                    label='Charterers'
                                    selectedContactId={invalidMarketInfoData.editItemParam.chartererId ? invalidMarketInfoData.editItemParam.chartererId : ""}
                                    useFreeText={true}
                                    onContactIdChange={(value: string) => {
                                        dispatch(setCorrectInvalidMarketInfoParam({
                                            ...invalidMarketInfoData.editItemParam,
                                            chartererId: value
                                        }));
                                    }}
                                >
                                </ContactSelector>
                            </Grid>
                        }
                        {
                            !invalidMarketInfoData.selectedItem.ownerActive &&
                            <Grid item xs={12} className="input-component">
                                <ContactSelector
                                    label='Charterers'
                                    selectedContactId={invalidMarketInfoData.editItemParam.ownerId ? invalidMarketInfoData.editItemParam.ownerId : ""}
                                    useFreeText={true}
                                    onContactIdChange={(value: string) => {
                                        dispatch(setCorrectInvalidMarketInfoParam({
                                            ...invalidMarketInfoData.editItemParam,
                                            ownerId: value
                                        }));
                                    }}
                                >
                                </ContactSelector>
                            </Grid>
                        }
                        {
                            !invalidMarketInfoData.selectedItem.cargoTypeActive &&
                            <Grid item xs={12} className="input-component">
                                <CargoTypeSelector
                                    className='cargo-input-component'
                                    isGroupDisabled={true}
                                    onChange={(cargoType: CargoTypeModel | null) => {
                                        if (cargoType) {
                                            dispatch(setCorrectInvalidMarketInfoParam({
                                                ...invalidMarketInfoData.editItemParam,
                                                cargoTypeId: (cargoType as CargoTypeModel).id
                                            }));
                                        }
                                    }}
                                    cargoTypeValue={invalidMarketInfoData.editItemParam.cargoTypeId ? getCargoTypeById(invalidMarketInfoData.editItemParam.cargoTypeId) : null}
                                >
                                </CargoTypeSelector>
                            </Grid>
                        }
                        {
                            !invalidMarketInfoData.selectedItem.loadPortActive &&
                            <Grid item xs={12} className="input-component">
                                <PortSelector
                                    onPortChange={(value: PortModel | null) => {
                                        if (value) {
                                            dispatch(setCorrectInvalidMarketInfoParam({
                                                ...invalidMarketInfoData.editItemParam,
                                                loadPort: (value as PortModel).id
                                            }));
                                        } else {
                                            dispatch(setCorrectInvalidMarketInfoParam({
                                                ...invalidMarketInfoData.editItemParam,
                                                loadPort: 0
                                            }));
                                        }
                                    }}
                                    className="cargo-input-component"
                                    portLabel='Loadport'
                                    areaLabel='Area'
                                    isAreaDisabled={true}
                                    portValue={invalidMarketInfoData.editItemParam.loadPort ? getPortById(invalidMarketInfoData.editItemParam.loadPort) : null}
                                    isAreaRelatedToPort={true}
                                >
                                </PortSelector>
                            </Grid>
                        }
                        {
                            !invalidMarketInfoData.selectedItem.disportActive &&
                            <Grid item xs={12} className="input-component">
                                <PortSelector
                                    onPortChange={(value: PortModel | null) => {
                                        if (value) {
                                            dispatch(setCorrectInvalidMarketInfoParam({
                                                ...invalidMarketInfoData.editItemParam,
                                                disport: (value as PortModel).id
                                            }));
                                        } else {
                                            dispatch(setCorrectInvalidMarketInfoParam({
                                                ...invalidMarketInfoData.editItemParam,
                                                disport: 0
                                            }));
                                        }
                                    }}
                                    className="cargo-input-component"
                                    portLabel='Disport'
                                    areaLabel='Area'
                                    isAreaDisabled={true}
                                    portValue={invalidMarketInfoData.editItemParam.disport ? getPortById(invalidMarketInfoData.editItemParam.disport) : null}
                                    isAreaRelatedToPort={true}
                                >
                                </PortSelector>
                            </Grid>
                        }
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={9}>
                                </Grid>
                                <Grid item xs={3}>
                                    <Button
                                        variant='contained'
                                        fullWidth
                                        onClick={() => {
                                            dispatch(showLoading(true));
                                            MarketInfoApi.CorrectInvalid(invalidMarketInfoData.editItemParam)
                                                .then((response: BaseResponse<MarketInfoModel>) => {
                                                    if (response.success) {

                                                        Utility.Alert.Success("Market Info updated successful");

                                                        dispatch(showLoading(false));

                                                        dispatch(setRefreshInvalidMarketInfoTable((new Date()).getTime()))

                                                        dispatch(setOpenEditInvalidMarketInfoModal(false));
                                                    }
                                                })
                                        }}
                                    >Save</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Box>
        </Modal>
    </div>
};

export default InvalidMarketInfoTable;